import {
  ShieldCheck,
  CheckCircle,
  DollarSign,
  Home,
  ThumbsUp,
  Ruler,
  CalendarClock,
  Hammer,
  Users,
  Wrench,
} from 'lucide-react';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/metal-roof.js';



export default function MetalRoofPage() {
  const service = {
    title: 'Metal Roofing for Ultimate Durability',
    image: '/hero/best-metal-roof-services-in-los-angeles.webp',
    description:
      'Metal roofing installation by Modern Build. Strong, fire-resistant, energy-efficient solutions. Free estimate: (323) 300-4130',
    link: '/services/metal-roof',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

      // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Metal Roof Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
      <Head>
        <title>Metal Roof Installation in Los Angeles | Free Estimate | Modern Build</title>
        <meta name="title" content="Metal Roof Installation in Los Angeles | Free Estimate | Modern Build" />
        <meta
          name="description"
          content="Metal roofing installation by Modern Build. Strong, fire-resistant, energy-efficient solutions. Free estimate: (323) 300-4130"
        />
        <meta
          name="keywords"
          content="metal roof Los Angeles, standing seam metal roofing, steel roofing, aluminum roof installation, fire-resistant roofing"
        />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/metal-roof" />

        <meta property="og:title" content="Metal Roof Installation in Los Angeles | Free Estimate | Modern Build" />
        <meta
          property="og:description"
          content="Metal roofing installation by Modern Build. Strong, fire-resistant, energy-efficient solutions. Free estimate: (323) 300-4130"
        />
        <meta
          property="og:image"
          content="https://modernbuildremodel.com/hero/best-metal-roof-services-in-los-angeles.webp"
        />
        <meta property="og:url" content="https://modernbuildremodel.com/services/metal-roof" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Metal Roof Installation in Los Angeles | Free Estimate | Modern Build" />
        <meta
          name="twitter:description"
          content="Metal roofing installation by Modern Build. Strong, fire-resistant, energy-efficient solutions. Free estimate: (323) 300-4130"
        />
        <meta
          name="twitter:image"
          content="https://modernbuildremodel.com/hero/best-metal-roof-services-in-los-angeles.webp"
        />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Metal Roof Installation in Los Angeles | Free Estimate | Modern Build',
                description:
                  'Get superior weather protection with metal roofing. Modern Build installs high-performance metal roofs for homes and commercial buildings in Los Angeles.',
                slug: '/services/metal-roof',
              })
            ),
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>

      <Header />
      <Hero category="metalRoof" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="DESIGNED TO LAST"
        title="Premium Metal Roof Installation in Los Angeles"
        background="white"
        description="Metal roofing is one of the most durable and low-maintenance roofing options available. From standing seam to corrugated and ribbed panels, we install metal roofs that are built to endure extreme temperatures, winds, and fire."
        extraText="Our installations include proper ventilation, insulation, and waterproofing for long-term protection."
        phone="(323) 300-4130"
        image1="/split/metal-roof-installation-los-angeles.webp"
        image2="/split/standing-seam-metal-roof-los-angeles.webp"
      />

<Benefits
  title="Why Choose Modern Build for Metal Roofing?"
  subtitle="Performance, protection, and peace of mind"
  items={[
    {
      icon: <ShieldCheck className="w-8 h-8 text-primary" />,
      title: "Work Performed by Licensed, Bonded & Insured Installers",
      href: "/about-us",
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-primary" />,
      title: "Standing Seam & Ribbed Panels",
    },
    {
      icon: <Wrench className="w-8 h-8 text-primary" />,
      title: "Complete Tear-Offs & Retrofits",
    },
    {
      icon: <Ruler className="w-8 h-8 text-primary" />,
      title: "Precision Fabrication & Fastening",
    },
    {
      icon: <Hammer className="w-8 h-8 text-primary" />,
      title: "Wind, Fire & Hail Resistant",
    },
    {
      icon: <DollarSign className="w-8 h-8 text-primary" />,
      title: "Lower Cooling Costs",
      href: "/services/energy-efficient-windows",
    },
    {
      icon: <ThumbsUp className="w-8 h-8 text-primary" />,
      title: "Modern & Industrial Aesthetic",
    },
    {
      icon: <Home className="w-8 h-8 text-primary" />,
      title: "Residential & Commercial Applications",
    },
  ]}
/>


      <ContentBlock
        subtitle="BUILT FOR CALIFORNIA WEATHER"
        title="Metal Roofing That Performs"
        description="Metal roofs are engineered to outlast traditional asphalt systems and provide superior energy savings. They're an ideal choice for California homes exposed to sun, fire zones, and intense storms."
        extraText="We help you choose the right profile and coating for your style, then handle every aspect of installation."
        buttonText="Request a Metal Roof Estimate"
        background="white"
      />

      <ProcessRoadmap
        title="Our Metal Roofing Process"
        subtitle="Engineered for precision and performance"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Consultation & Inspection",
            description: "We assess your existing roof and help select the best metal roofing system for your home.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Material Selection",
            description: "Choose from standing seam, corrugated, or metal shingles in your preferred color and finish.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Expert Installation",
            description: "We fabricate panels to fit your roof dimensions and install using weather-tight fastening methods.",
          },
          {
            icon: <CheckCircle className="w-10 h-10 text-primary" />,
            title: "Final Review",
            description: "We inspect the system, confirm all flashing and fasteners, and leave your home spotless.",
          },
        ]}
      />

      <Split
        subtitle="FIRE-RESISTANT, SLEEK, AND STRONG"
        title="A Smarter Investment in Roofing"
        background="white"
        description="Metal roofs not only deliver unmatched protection — they also enhance the visual appeal and resale value of your property. Plus, many systems qualify for cool roof rebates in California."
        extraText="We offer 40+ year warranties on most metal roof systems."
        phone="(323) 300-4130"
        image1="/split/modern-metal-roof-los-angeles.webp"
        image2="/split/metal-roof-replacement-los-angeles.webp"
        reverse={true}
      />

      <CTASection
        title="Book Your Free Metal Roof Consultation"
        subtitle="Built for longevity, installed with precision"
        buttonText="Schedule Now"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="metal-roof" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}
